import React, { useMemo, useState } from 'react'
import { Box, Button, Divider, Grid } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ViewTaskTable from '../Components/Task/TaskComponents/ViewTaskTable'
import SearchInput from '../Components/Common/SearchInput'
import { backgroundColors } from '../Components/Common/Color/Color'
import { useAppSelector } from '../redux/store'
import useFetchAssignedTaskDetails from '../hooks/taskHooks/useFetchAssignedTaskDetails'
import TaskFilter from '../Components/Task/TaskComponents/TaskFilter'
import { selectUserTeamMemberInfo } from '../redux/auth/selector'
import { selectFetchAssignedTask } from '../redux/task/selector'
import { processViewTasksData } from '../utils/taskUtils'

const ViewTaskPage: React.FC = () => {
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)

  const [filters, setFilters] = useState<{
    Status: string[]
    Importance: string[]
  }>({
    Status: [],
    Importance: [],
  })

  const assignedFilters = useMemo(
    () => ({
      teamMemberEmail: teamMemberDetails.email,
      teamMemberWorkdayId: teamMemberDetails.workdayId,
    }),
    [teamMemberDetails.email, teamMemberDetails.workdayId],
  )

  // Get tasks directly from Redux store for real-time updates
  const tasksFromStore = useAppSelector(selectFetchAssignedTask)
  
  // Use hook for initial fetch and manual refresh
  const { tasks: hookTasks } = useFetchAssignedTaskDetails(assignedFilters, refreshTrigger)
  
  // Use store data if available, otherwise use hook data
  const tasks = tasksFromStore.length > 0 ? tasksFromStore : hookTasks

  const handleFilterChange = (selectedFilters: Record<string, string[] | undefined>) => {
    setFilters({
      Status: selectedFilters.Status ?? [],
      Importance: selectedFilters.Importance ?? [],
    })
  }

  const searchData = useMemo(() => {
    return processViewTasksData(tasks, filters.Status, filters.Importance, searchQuery)
  }, [tasks, filters.Status, filters.Importance, searchQuery])

  const handleRefreshData = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  return (
    <Box
      component='div'
      sx={{ p: 0, backgroundColor: backgroundColors.white, width: 'auto' }}
      borderRadius={2}
      boxShadow={1}
      data-testid="view-task-page"
    >
      <Grid
        container
        sx={{ p: 2 }}
        flexDirection='row'
        alignItems={'center'}
        justifyContent='flex-end'
      >
        <Grid>
          <Grid container spacing={2} alignItems='center' display='flex'>
            <Grid>
              <Button
                id='refresh-button'
                color='success'
                startIcon={<RefreshIcon />}
                onClick={handleRefreshData}
                size='small'
                data-testid="view-task-refresh-button"
              >
                Refresh
              </Button>
            </Grid>
            <Grid>
              <TaskFilter onFilterChange={handleFilterChange} pageName='viewTaskPage' data-testid="view-task-filter" />
            </Grid>
            <Grid>
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
                data-testid="view-task-search"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <ViewTaskTable data={searchData} refreshTable={Boolean(searchQuery)} />
    </Box>
  )
}

export default ViewTaskPage
